/**
 * Time-value-of-money arithmetic shared by the finance calculators.
 *
 * Conventions, stated once so every page can point here:
 *
 * - Rates are decimals (0.05 is 5%). `annualRate` is a *nominal* annual rate; the
 *   periodic rate is `annualRate / perYear`. `perYear = Infinity` means continuous
 *   compounding.
 * - Cash flows are positive numbers in the direction the caller names. Excel's
 *   `FV`/`PV`/`PMT` flip signs so the result is an outflow; the functions here return
 *   magnitudes, and the tests compare against Excel with the sign flipped.
 * - `timing` is `"end"` (ordinary annuity, Excel type 0) or `"begin"` (annuity due,
 *   Excel type 1).
 */

export type Timing = "end" | "begin";

/** Passing this as `perYear` compounds continuously. */
export const CONTINUOUS = Number.POSITIVE_INFINITY;

/** Growth factor over `years` at a nominal annual rate compounded `perYear` times. */
export function growthFactor(
  annualRate: number,
  perYear: number,
  years: number
): number {
  if (perYear === CONTINUOUS) {
    return Math.exp(annualRate * years);
  }
  return (1 + annualRate / perYear) ** (perYear * years);
}

/** Effective annual rate (APY) for a nominal annual rate (APR). */
export function effectiveAnnualRate(
  annualRate: number,
  perYear: number
): number {
  return growthFactor(annualRate, perYear, 1) - 1;
}

/** The nominal annual rate that compounds `perYear` times to an effective annual rate. */
export function nominalFromEffective(
  effective: number,
  perYear: number
): number {
  if (perYear === CONTINUOUS) {
    return Math.log(1 + effective);
  }
  return perYear * ((1 + effective) ** (1 / perYear) - 1);
}

/** (1 − (1+i)^−n) / i — the present value of 1 per period for n periods. */
export function annuityFactor(i: number, n: number): number {
  if (i === 0) {
    return n;
  }
  return (1 - (1 + i) ** -n) / i;
}

/** ((1+i)^n − 1) / i — the future value of 1 per period for n periods. */
export function accumulationFactor(i: number, n: number): number {
  if (i === 0) {
    return n;
  }
  return ((1 + i) ** n - 1) / i;
}

function dueFactor(i: number, timing: Timing): number {
  return timing === "begin" ? 1 + i : 1;
}

/** Future value of a single amount: PV (1+i)^n. */
export function futureValueLump(pv: number, i: number, n: number): number {
  return pv * (1 + i) ** n;
}

/** Present value of a single amount due in n periods: FV / (1+i)^n. */
export function presentValueLump(fv: number, i: number, n: number): number {
  return fv / (1 + i) ** n;
}

/** Future value of `n` equal payments of `pmt`. */
export function futureValueAnnuity(
  pmt: number,
  i: number,
  n: number,
  timing: Timing = "end"
): number {
  return pmt * accumulationFactor(i, n) * dueFactor(i, timing);
}

/** Present value of `n` equal payments of `pmt`. */
export function presentValueAnnuity(
  pmt: number,
  i: number,
  n: number,
  timing: Timing = "end"
): number {
  return pmt * annuityFactor(i, n) * dueFactor(i, timing);
}

/**
 * Present value of a perpetuity whose first payment is `pmt`, growing `g` per period.
 * Needs i > g; returns null otherwise (the sum diverges).
 */
export function presentValuePerpetuity(
  pmt: number,
  i: number,
  g = 0,
  timing: Timing = "end"
): number | null {
  if (i <= g) {
    return null;
  }
  const ordinary = pmt / (i - g);
  return timing === "begin" ? ordinary * (1 + i) : ordinary;
}

/**
 * The level payment that amortizes `pv` over `n` periods, leaving `fv` at the end.
 * The payment that Excel's PMT returns, with the sign flipped.
 */
export function levelPayment(
  pv: number,
  i: number,
  n: number,
  timing: Timing = "end",
  fv = 0
): number {
  const needed = pv - fv / (1 + i) ** n;
  return needed / (annuityFactor(i, n) * dueFactor(i, timing));
}

/** Round to a number of decimals, half away from zero, without the 1.005 trap. */
export function roundTo(value: number, digits: number): number {
  const factor = 10 ** digits;
  const scaled = Math.abs(value) * factor;
  const rounded = Math.round(scaled * (1 + Number.EPSILON));
  return (Math.sign(value) * rounded) / factor;
}
